import { Button, Flex, UseDisclosureProps } from '@chakra-ui/react';
import React from 'react';
import { ArrowLeft } from '@kula-ai/icons-core';
import { useKFlags } from '@/hooks/use-k-flags';
import { AiScoringConstants } from '../aiScoringConstants';
import { ProcessedAttribute } from '../utils/attributeValidation';

const {
  STEP_ICP,
  STEP_ATTRIBUTES,
  STEP_TESTING,
  ICP_MIN_LENGTH,
  MIN_ATTRIBUTE_LENGTH,
} = AiScoringConstants;

// Enhanced helper to check if attributes have meaningful content
// This aligns with the main validation logic in isAttributeListValid
const hasAttributesMeaningfulContent = (
  attributes: any[],
  interactedAttributes?: Set<number>
): boolean => {
  if (!attributes || attributes.length === 0) return false;

  // Filter out empty new attributes that haven't been interacted with
  // This matches the logic in isAttributeListValid
  const relevantAttributes = attributes.filter((attr) => {
    const hasBeenInteracted = interactedAttributes
      ? interactedAttributes.has(attr.id)
      : false;
    const isEmpty = attr.description?.trim().length === 0;

    // Ignore new attributes that are empty and haven't been interacted with
    if (attr.isNewAttribute && isEmpty && !hasBeenInteracted) {
      return false;
    }

    return true;
  });

  // Check minimum required attributes with meaningful content
  const meaningfulAttributes = relevantAttributes.filter(
    (attr) => attr.description?.trim().length >= MIN_ATTRIBUTE_LENGTH
  );

  return meaningfulAttributes.length >= 3; // MIN_ATTRIBUTES_REQUIRED
};

export interface FooterButtonConfig {
  key: string;
  label: React.ReactNode;
  variant: string;
  onClick: () => void;
  loadingText?: string;
}

interface AiScoringDrawerFooterProps {
  activeStep: number;
  onCancel: () => void;
  onBack: () => void;
  onSave: () => void;
  onSkipAndSave: () => void;
  onVerifyIcp: () => void;
  onVerifyAttributes: () => void;
  isDirty?: boolean;
  /** Auto-reject (threshold/toggle) changed — must keep save enabled even
   * when the attributes themselves are untouched (ENG-3049). */
  isAutoRejectDirty?: boolean;
  isLoading?: boolean;
  loadingKey?: string | null;
  isValid?: boolean;
  hasUnsavedGeneratedAttributes?: boolean;
  isEditMode?: boolean;
  hasValidatedButNotSaved?: boolean;
  icpData?: { data: string; categories: string[]; isModified: boolean };
  attributes?: ProcessedAttribute[];
  interactedAttributes?: Set<number>;
  cancelBtnDisclosure?: UseDisclosureProps;
}

export const AiScoringDrawerFooter: React.FC<AiScoringDrawerFooterProps> = ({
  activeStep,
  onCancel: _onCancel,
  onBack,
  onSave,
  onSkipAndSave,
  onVerifyIcp,
  onVerifyAttributes,
  isDirty = false,
  isAutoRejectDirty = false,
  isLoading = false,
  loadingKey = null,
  isValid = true,
  hasUnsavedGeneratedAttributes = false,
  isEditMode = false,
  hasValidatedButNotSaved = false,
  icpData,
  attributes = [],
  interactedAttributes,
  cancelBtnDisclosure,
}) => {
  const { feJobTeammates } = useKFlags();

  const cancelConfig = {
    key: 'cancel',
    label: 'Cancel',
    variant: 'secondary',
    onClick: cancelBtnDisclosure.onOpen,
  };

  const getFooterButtons = (): {
    left: FooterButtonConfig[];
    right: FooterButtonConfig[];
  } => {
    if (activeStep === STEP_ICP) {
      return {
        left: [],
        right: [
          cancelConfig,
          {
            key: 'verifyIcp',
            label: 'Next: Verify attributes',
            loadingText: 'Updating...',
            variant: 'primary',
            onClick: onVerifyIcp,
          },
        ],
      };
    }
    if (activeStep === STEP_ATTRIBUTES) {
      return {
        left: [
          {
            key: 'backToIcp',
            label: (
              <>
                <ArrowLeft
                  size="1em"
                  className="kula-icon"
                  style={{ marginRight: '4px' }}
                />
                Back: Define ICP
              </>
            ),
            variant: 'quaternary',
            onClick: onBack,
          },
        ],
        right: [
          {
            key: 'skipAndSave',
            label: 'Skip test and save',
            loadingText: 'Saving...',
            variant: 'tertiary',
            onClick: onSkipAndSave,
          },
          {
            key: 'verifyAttributes',
            // Copy must match the drawer's "Test Scoring agent" step label
            label: feJobTeammates
              ? 'Next: Test Scoring agent'
              : 'Next: Test AI scoring',
            loadingText: 'Updating...',
            variant: 'primary',
            onClick: onVerifyAttributes,
          },
        ],
      };
    }
    if (activeStep === STEP_TESTING) {
      return {
        left: [
          {
            key: 'backToAttributes',
            label: (
              <>
                <ArrowLeft
                  size="1em"
                  className="kula-icon"
                  style={{ marginRight: '4px' }}
                />
                Back: Verify attributes
              </>
            ),
            variant: 'quaternary',
            onClick: onBack,
          },
        ],
        right: [
          cancelConfig,
          {
            key: 'saveAndFinish',
            label: 'Save',
            variant: 'primary',
            onClick: onSave,
          },
        ],
      };
    }
    return { left: [], right: [] };
  };

  const { left: leftButtons, right: rightButtons } = getFooterButtons();

  return (
    <>
      <Flex
        justifyContent="space-between"
        w="100%"
        flexShrink={0}
        borderTop="1px solid"
        borderColor="secondary-100"
        p="12px"
        gap="8px"
      >
        <Flex gap="8px">
          {leftButtons.map((btn) => {
            const isBtnLoading = false;
            let isBtnDisabled = false;

            if (btn.key === 'backToIcp' || btn.key === 'backToAttributes') {
              // Back button logic: disabled during any loading operation
              isBtnDisabled = isLoading;
            }

            return (
              <Button
                key={btn.key}
                variant={btn.variant}
                onClick={btn.onClick}
                isLoading={isBtnLoading}
                disabled={isBtnDisabled}
                loadingText={btn.loadingText}
              >
                {btn.label}
              </Button>
            );
          })}
        </Flex>
        <Flex gap="8px">
          {rightButtons.map((btn) => {
            let isBtnLoading = false;
            let isBtnDisabled = false;

            if (btn.key === 'verifyIcp') {
              // Hook sets loadingKey as 'verifyIcp'
              isBtnLoading = isLoading && loadingKey === 'verifyIcp';
              // Disable button during any loading operation or if ICP doesn't meet minimum length
              // This matches the tab validation logic for step 2 (STEP_ATTRIBUTES)
              const icpLength = icpData?.data?.trim().length || 0;
              isBtnDisabled = isLoading || icpLength < ICP_MIN_LENGTH;
            } else if (btn.key === 'skipAndSave') {
              // Hook sets loadingKey as 'skip' for skip and save action
              isBtnLoading = isLoading && loadingKey === 'skip';
              // Skip button logic:
              // - Disabled if any loading is happening
              // - Disabled if content doesn't have valid meaningful content (prevents space-only saves)
              // - Disabled if there are validation errors (same as Next button)
              // - In create mode: enabled on mount (allows skipping without changes) IF has valid content
              // - In edit mode: disabled on mount, only enabled when has meaningful unsaved changes

              // Check for meaningful content requirements for skipping
              const hasMeaningfulContent = hasAttributesMeaningfulContent(
                attributes,
                interactedAttributes
              );

              if (isEditMode) {
                // Edit mode: disabled on mount, only enable when has meaningful changes or validated content
                // Auto-reject-only changes count as savable too (ENG-3049)
                isBtnDisabled =
                  isLoading ||
                  !isValid ||
                  !hasMeaningfulContent ||
                  (!isDirty &&
                    !hasUnsavedGeneratedAttributes &&
                    !hasValidatedButNotSaved &&
                    !isAutoRejectDirty);
              } else {
                // Create mode: enabled when has meaningful content and no validation errors
                isBtnDisabled = isLoading || !isValid || !hasMeaningfulContent;
              }
            } else if (btn.key === 'verifyAttributes') {
              // Hook sets loadingKey as 'next' for verify attributes action
              isBtnLoading = isLoading && loadingKey === 'next';
              // Next button logic for step 3 (Test AI scoring):
              // - Disabled if any loading is happening
              // - Disabled if content doesn't have valid meaningful content (prevents space-only validation)
              // - Disabled if we're in step 2 and trying to go to step 3 (need attributes to be valid first)
              // - In edit mode: Only enabled when user has made meaningful changes (not on mount)
              // - In create mode: enabled when has valid content

              // Check for meaningful content requirements (prevents "just typed a space" issue)
              const hasMeaningfulContent = hasAttributesMeaningfulContent(
                attributes,
                interactedAttributes
              );

              if (isEditMode) {
                // Edit mode: Only enable when user has made meaningful changes, disabled on mount
                isBtnDisabled =
                  isLoading ||
                  !isValid ||
                  !hasMeaningfulContent ||
                  !(
                    isDirty ||
                    hasUnsavedGeneratedAttributes ||
                    hasValidatedButNotSaved
                  );
              } else {
                // Create mode: enabled when has meaningful valid content
                isBtnDisabled = isLoading || !isValid || !hasMeaningfulContent;
              }
            } else if (btn.key === 'saveAndFinish') {
              // Hook sets loadingKey as 'save' for save action
              isBtnLoading = isLoading && loadingKey === 'save';
              // Save button logic:
              // since the attributes are valid here, we can simply save here
              isBtnDisabled = false;
            } else if (btn.key === 'cancel') {
              // Cancel button logic: disabled during any loading operation
              isBtnDisabled = isLoading;
            }

            return (
              <Button
                key={btn.key}
                variant={btn.variant}
                onClick={btn.onClick}
                isLoading={isBtnLoading}
                disabled={isBtnDisabled}
                loadingText={btn.loadingText}
              >
                {btn.label}
              </Button>
            );
          })}
        </Flex>
      </Flex>
    </>
  );
};
